import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { Loading } from './../../../Common/Loading'
import { InstructorBase } from './../../../Layouts/Instructor/V2/InstructorBase'
import { Confirm } from './../../../Common/Modals/Confirm'
import { SortIndicator } from './../../../Common/Tables/SortIndicator'
import { SortHelpText } from './../../../Common/Tables/SortHelpText'

import { LogErrors } from './../../../../utilities/LogErrors'
import { usePersistedState } from './../../../../utilities/usePersistedState'
import { ValidateSession } from './../../../../utilities/BL/Sessions'
import { useFloatMessage, MESSAGE_TYPE } from './../../../Providers/FloatMessage'
import {
    GetLicensesSummary,
    GetAssignedLicenses,
    GetInstructorLicenseDetail,
    UpdateLicensesBatch,
    RemoveLicensesBatch,
    GetCoursesForAssign
} from './../../../../utilities/BL/Leader/Licenses'
import { WEBPAGES } from './../../../../utilities/EL/WebPages'
import { ROLES } from './../../../../utilities/EL/Roles'

// Excel utils
import { BuildAndDownloadWorkbook, BuildSheetWithMeta } from './../../../../utilities/BL/Instructor/ExcelExporter'

// Los botones de acción sobre la selección comparten fila. Se ajustan al ancho de su
// texto y se impide el salto de línea para que cada etiqueta quepa en un solo renglón.
const EXTRA_BUTTON_STYLE = {
    fontSize: '0.9rem',
    whiteSpace: 'nowrap',
    width: 'auto',
    padding: '9.9px 20px'
}

// Valores que se muestran en cada columna de la tabla "Licencias Asignadas por Instructor"
// (los nombres de los campos pueden variar según lo que mande el backend).
const GetInstructorName = (row) => row.instructorName || row.fullName || row.name || row.instructor
const GetUserName = (row) => row.username || row.user

// Igual que en "Elige un curso" de /leader/licenses/assign: nombre del curso + su nivel.
const BuildCourseNameWithLevel = (row, coursesCatalog) => {
    const course = coursesCatalog.find(c => c.id === row.idCourse)
    const courseName = row.courseName || row.course || row.name
    return course ? `${courseName} (Nivel ${course.level})` : courseName
}

// Valor por el que se ordena cada columna al pulsar su encabezado: es el mismo que se muestra.
const SORT_VALUE_BY_COLUMN = {
    instructorName: (row) => GetInstructorName(row) ?? '',
    user: (row) => GetUserName(row) ?? '',
    courseName: (row, coursesCatalog) => BuildCourseNameWithLevel(row, coursesCatalog) ?? '',
    licenses: (row) => Number(row.licenses) || 0
}

const Licenses = () => {
    const navigate = useNavigate()
    const { showMessage } = useFloatMessage()
    const [isLoading, setIsLoading] = useState(true)
    const [licensesSummary, setLicensesSummary] = useState({
        contracted: 0,
        assigned: 0,
        used: 0,
        remaining: 0
    })
    const [instructorLicenses, setInstructorLicenses] = useState([])
    const [sortConfig, setSortConfig] = usePersistedState('leaderLicenses.sort', { key: null, direction: 'asc' })
    const [coursesCatalog, setCoursesCatalog] = useState([])
    const [selectedLicenseIds, setSelectedLicenseIds] = useState([])
    const [editedLicenses, setEditedLicenses] = useState({})
    const [isSubmitting, setIsSubmitting] = useState(false)

    const [showConfirmationModal, setShowConfirmationModal] = useState(false)
    const [confirmModalMessage, setConfirmModalMessage] = useState('')
    const [licensesBatchToUpdate, setLicensesBatchToUpdate] = useState([])

    const [showUnlinkModal, setShowUnlinkModal] = useState(false)
    const [unlinkModalMessage, setUnlinkModalMessage] = useState('')
    const [licensesBatchToUnlink, setLicensesBatchToUnlink] = useState([])

    useEffect(() => {
        async function ValidateAndInit() {
            try {
                const tmpSession = await ValidateSession()
                if (tmpSession?.success && tmpSession.data?.user?.userStatus === 2) {
                    const [resSummary, resLicenses, resCourses] = await Promise.all([
                        GetLicensesSummary(),
                        GetAssignedLicenses(),
                        GetCoursesForAssign()
                    ])
                    if (resSummary.success) {
                        setLicensesSummary(resSummary.data)
                    }
                    if (resLicenses.success) {
                        setInstructorLicenses(resLicenses.data || [])
                    }
                    if (resCourses.success) {
                        setCoursesCatalog(resCourses.data || [])
                    }
                    setIsLoading(false)
                } else {
                    navigate('/confirm')
                }
            } catch (error) {
                await LogErrors('ValidateAndInit - Leaders Licenses', error)
                navigate(`/?logout=${process.env.REACT_APP_LOGOUT_PARAMETER}`)
            }
        }
        ValidateAndInit()
    }, [navigate])

    const HandleUpdateLicensesClick = async () => {
        try {
            setIsSubmitting(true)
            
            // 1. Validar que todas las cantidades ingresadas sean números enteros positivos válidos
            const batchItems = []
            for (const key of selectedLicenseIds) {
                const row = instructorLicenses.find(r => `${r.idCenterUser}-${r.idCourse}` === key)
                if (!row) continue

                const editedQty = editedLicenses[key]
                if (!editedQty) {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: `Por favor ingresa la cantidad de licencias para ${row.instructorName}.` })
                    setIsSubmitting(false)
                    return
                }

                const qtyInt = parseInt(editedQty, 10)
                if (isNaN(qtyInt) || qtyInt <= 0 || String(qtyInt) !== editedQty) {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: 'La cantidad de licencias debe ser un número entero positivo.' })
                    setIsSubmitting(false)
                    return
                }

                batchItems.push({
                    row,
                    quantity: qtyInt
                })
            }

            if (batchItems.length === 0) {
                setIsSubmitting(false)
                return
            }

            // 2. Obtener idInstructorLicense real para cada ítem en el lote (si no viene ya en la fila)
            const promises = batchItems.map(async (item) => {
                if (item.row.idInstructorLicense) {
                    return { idInstructorLicense: item.row.idInstructorLicense, quantity: item.quantity, row: item.row }
                } else {
                    const res = await GetInstructorLicenseDetail(item.row.idCenterUser, item.row.idCourse)
                    if (res.success && res.data?.idInstructorLicense) {
                        return { idInstructorLicense: res.data.idInstructorLicense, quantity: item.quantity, row: item.row }
                    }
                    throw new Error(res.message || `No se pudo obtener el ID de licencia para ${item.row.instructorName}.`)
                }
            })

            const resolvedBatch = await Promise.all(promises)

            // 3. Formatear mensaje del modal de confirmación
            let message = ''
            if (resolvedBatch.length === 1) {
                const item = resolvedBatch[0]
                message = `¿Estás segur@ de actualizar a ${item.quantity} el número de licencias al Instructor ${item.row.instructorName} para el curso ${item.row.courseName}?`
            } else {
                message = `¿Estás segur@ de actualizar el número de licencias a los Instructores?`
            }

            setLicensesBatchToUpdate(resolvedBatch.map(item => ({ idInstructorLicense: item.idInstructorLicense, quantity: item.quantity })))
            setConfirmModalMessage(message)
            setShowConfirmationModal(true)

        } catch (error) {
            await LogErrors('HandleUpdateLicensesClick - Licenses UI', error)
            showMessage({ type: MESSAGE_TYPE.BAD, text: error.message || 'Ocurrió un error al validar los datos de las licencias.' })
        } finally {
            setIsSubmitting(false)
        }
    }

    const ExecuteUpdateLicenses = async () => {
        try {
            setIsSubmitting(true)
            const response = await UpdateLicensesBatch(licensesBatchToUpdate)

            setShowConfirmationModal(false)
            setLicensesBatchToUpdate([])
            setConfirmModalMessage('')

            if (response.success) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Se actualizó el número de licencias correctamente.' })
                // Limpiar selección
                setSelectedLicenseIds([])
                setEditedLicenses({})
                
                // Refrescar los datos de la página
                setIsLoading(true)
                const [resSummary, resLicenses] = await Promise.all([
                    GetLicensesSummary(),
                    GetAssignedLicenses()
                ])
                if (resSummary.success) {
                    setLicensesSummary(resSummary.data)
                }
                if (resLicenses.success) {
                    setInstructorLicenses(resLicenses.data || [])
                }
                setIsLoading(false)
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: response.message || 'No fue posible actualizar el número de licencias.' })
            }
            return { success: true }
        } catch (error) {
            await LogErrors('ExecuteUpdateLicenses - Licenses UI', error)
            setShowConfirmationModal(false)
            setLicensesBatchToUpdate([])
            setConfirmModalMessage('')
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'No fue posible actualizar el número de licencias.' })
            return { success: true }
        } finally {
            setIsSubmitting(false)
        }
    }

    const HandleUnlinkLicensesClick = () => {
        try {
            // Armar el lote con los pares instructor/curso seleccionados
            const batchItems = []
            for (const key of selectedLicenseIds) {
                const row = instructorLicenses.find(r => `${r.idCenterUser}-${r.idCourse}` === key)
                if (!row) continue

                batchItems.push({
                    idCenterUser: row.idCenterUser,
                    idCourse: row.idCourse,
                    row
                })
            }

            if (batchItems.length === 0) return

            // Formatear mensaje del modal de confirmación
            let message = ''
            if (batchItems.length === 1) {
                const item = batchItems[0]
                message = `¿Está segur@ de desvincular las licencias del Instructor ${item.row.instructorName} para el curso ${item.row.courseName}?`
            } else {
                message = `¿Está segur@ de desvincular las licencias de los ${batchItems.length} registros seleccionados?`
            }

            setLicensesBatchToUnlink(batchItems.map(item => ({ idCenterUser: item.idCenterUser, idCourse: item.idCourse })))
            setUnlinkModalMessage(message)
            setShowUnlinkModal(true)
        } catch (error) {
            LogErrors('HandleUnlinkLicensesClick - Licenses UI', error)
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'No fue posible desvincular las licencias.' })
        }
    }

    const ExecuteUnlinkLicenses = async () => {
        try {
            setIsSubmitting(true)
            const response = await RemoveLicensesBatch(licensesBatchToUnlink)

            setShowUnlinkModal(false)
            setLicensesBatchToUnlink([])
            setUnlinkModalMessage('')

            if (response.success) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Las licencias se desvincularon correctamente.' })

                // Limpiar selección
                setSelectedLicenseIds([])
                setEditedLicenses({})

                // Refrescar los datos de la página
                setIsLoading(true)
                const [resSummary, resLicenses] = await Promise.all([
                    GetLicensesSummary(),
                    GetAssignedLicenses()
                ])
                if (resSummary.success) {
                    setLicensesSummary(resSummary.data)
                }
                if (resLicenses.success) {
                    setInstructorLicenses(resLicenses.data || [])
                }
                setIsLoading(false)
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: 'No fue posible desvincular las licencias.' })
            }
            return { success: true }
        } catch (error) {
            await LogErrors('ExecuteUnlinkLicenses - Licenses UI', error)
            setShowUnlinkModal(false)
            setLicensesBatchToUnlink([])
            setUnlinkModalMessage('')
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'No fue posible desvincular las licencias.' })
            return { success: true }
        } finally {
            setIsSubmitting(false)
        }
    }

    const HandleExport = async () => {
        try {
            setIsLoading(true)

            if (!instructorLicenses?.length) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: 'No hay datos para exportar en la tabla.' })
                return
            }

            showMessage({ type: MESSAGE_TYPE.INFO, text: 'Exportando tabla...' })

            // Mapear los datos de las filas
            const rows = instructorLicenses.map(r => ({
                instructorName: r.instructorName || '',
                username: r.username || '',
                courseName: GetCourseNameWithLevel(r),
                licenses: r.licenses || 0
            }))

            const headerOrder = ['instructorName', 'username', 'courseName', 'licenses']
            const headerLabels = {
                instructorName: 'Nombre del Instructor',
                username: 'Usuario',
                courseName: 'Nombre del curso',
                licenses: 'Número de licencias'
            }
            const colWidths = [30, 20, 38, 18]

            const metaRows = [
                ['Reporte:', 'Licencias asignadas por Instructor'],
                ['Fecha de exportación:', new Date().toISOString().slice(0, 10)],
                ['Total registros:', rows.length]
            ]

            const ws = BuildSheetWithMeta({ metaRows, rows, headerOrder, headerLabels, colWidths })

            BuildAndDownloadWorkbook(`Licencias_Asignadas_${new Date().toISOString().slice(0, 10)}.xlsx`, [
                { name: 'Licencias', ws }
            ])

            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Reporte listo para descargar.' })
        } catch (error) {
            await LogErrors('HandleExport - Licenses UI', error)
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'No ha sido posible exportar la tabla.' })
        } finally {
            setIsLoading(false)
        }
    }

    const GetCourseNameWithLevel = (row) => BuildCourseNameWithLevel(row, coursesCatalog)

    // Ordenamiento al pulsar los encabezados: primer clic ascendente, segundo descendente.
    const HandleSort = (key) => {
        setSortConfig((prev) =>
            prev.key === key
                ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
                : { key, direction: 'asc' }
        )
    }

    // Texto con localeCompare en modo numérico ("Grupo 2" antes que "Grupo 10") y números por resta.
    const sortedInstructorLicenses = useMemo(() => {
        const getValue = SORT_VALUE_BY_COLUMN[sortConfig.key]
        if (!getValue) return instructorLicenses

        return [...instructorLicenses].sort((a, b) => {
            const aVal = getValue(a, coursesCatalog)
            const bVal = getValue(b, coursesCatalog)

            const result = (typeof aVal === 'string' && typeof bVal === 'string')
                ? aVal.localeCompare(bVal, undefined, { numeric: true })
                : aVal - bVal

            return sortConfig.direction === 'asc' ? result : -result
        })
    }, [instructorLicenses, coursesCatalog, sortConfig])

    return isLoading ? (
        <Loading />
    ) : (
        <InstructorBase urlToGo={'/dashboard'} page={WEBPAGES.LEADER.ZTUDENTS} role={ROLES.LEADER}>
            <section className='container Instructor__Instructors Container-FullHeight'>
                <p className='Instructor__Title Instructor__Title--Center'>Licencias Ztudia</p>

                <div className='container mt-5 Tables--Shadow Tables--Background p-4' style={{ maxWidth: '600px', borderRadius: '15px' }}>
                    <p className='Instructor__Subtitle Instructor__Subtitle--Center mb-4 border-bottom pb-2 fw-bold text-uppercase' style={{ letterSpacing: '0.05em' }}>
                        Resumen de Licencias
                    </p>

                    <div className='d-flex flex-column gap-3 px-3 py-2'>
                        <div className='d-flex align-items-center border-bottom pb-2'>
                            <span className='Instructor__Text--Bold fs-5 flex-grow-1 text-nowrap'>Licencias Contratadas:</span>
                            <span className='Instructor__Text fs-5 text-end' style={{ width: '70px' }}>{licensesSummary.contracted}</span>
                            <span style={{ width: '170px' }}></span>
                        </div>
                        <div className='d-flex align-items-center border-bottom pb-2'>
                            <span className='Instructor__Text--Bold fs-5 flex-grow-1 text-nowrap'>Licencias Asignadas:</span>
                            <span className='Instructor__Text fs-5 text-end' style={{ width: '70px' }}>{licensesSummary.assigned}</span>
                            <span style={{ width: '170px' }}></span>
                        </div>
                        <div className='d-flex align-items-center border-bottom pb-2'>
                            <span className='Instructor__Text--Bold fs-5 flex-grow-1 text-nowrap'>Licencias Utilizadas:</span>
                            <span className='Instructor__Text fs-5 text-end' style={{ width: '70px' }}>{licensesSummary.used}</span>
                            <span className='d-flex justify-content-end' style={{ width: '170px' }}>
                                <button
                                    type='button'
                                    className='Btn__Ztudia--PrimaryOutline'
                                    style={{ width: 'auto', padding: '9.9px 20px', margin: 0 }}
                                    onClick={() => navigate('/leader/licenses/used')}
                                >
                                    Detalles
                                </button>
                            </span>
                        </div>
                        <div className='d-flex align-items-center pb-2'>
                            <span className='Instructor__Text--Bold fs-5 flex-grow-1 text-nowrap'>Licencias Restantes:</span>
                            <span className='Instructor__Text fs-5 fw-bold text-success text-end' style={{ width: '70px' }}>{licensesSummary.remaining}</span>
                            <span style={{ width: '170px' }}></span>
                        </div>
                    </div>
                </div>

                <div className='Instructor__Text--Center'>
                    <button type='button' className='Btn__Ztudia--PrimaryOutline Instructor__SpaceUp Instructor__CreateButton' onClick={() => navigate('/leader/licenses/assign')}>
                        Asignar Licencias
                    </button>
                </div>

                <p className='Instructor__Subtitle Instructor__Subtitle--Center Instructor__SpaceUp'>
                    Licencias Asignadas por Instructor
                </p>

                <div className='d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2 Instructor__SpaceUp' style={{ maxWidth: '1180px', margin: '24px auto 0 auto' }}>
                    {selectedLicenseIds.length > 0 ? (
                        <div className='Tables__ExtraButtons m-0'>
                            <button
                                type='button'
                                className='Btn__Ztudia--PrimaryOutline Tables__ExtraButtons__Button'
                                style={EXTRA_BUTTON_STYLE}
                                onClick={HandleUpdateLicensesClick}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Procesando...' : 'Actualizar Licencias'}
                            </button>
                            <button
                                type='button'
                                className='Btn__Ztudia--PrimaryOutline Tables__ExtraButtons__Button'
                                style={EXTRA_BUTTON_STYLE}
                                onClick={HandleUnlinkLicensesClick}
                                disabled={isSubmitting}
                            >
                                Desvincular Licencias
                            </button>
                        </div>
                    ) : (
                        <div></div>
                    )}

                    <button
                        type='button'
                        className='Btn__Ztudia--PrimaryOutline Instructor__Results__Reports__ExportButton'
                        style={{ margin: 0 }}
                        onClick={HandleExport}
                        disabled={instructorLicenses.length === 0}
                    >
                        <i className='bi bi-filetype-xlsx'></i> Exportar
                    </button>
                </div>

                {instructorLicenses.length === 0 ? (
                    <div className='container mt-3 Tables--Shadow Tables--Background p-4 text-center' style={{ maxWidth: '1180px', borderRadius: '15px' }}>
                        <p className='Instructor__Text--Center m-0'>No hay licencias asignadas actualmente.</p>
                    </div>
                ) : (
                    <>
                    {/* Texto de ayuda arriba de la tarjeta de la tabla, con el mismo ancho y alineación que ella */}
                    <div style={{ maxWidth: '1180px', margin: '16px auto 0 auto' }}>
                        <SortHelpText />
                    </div>

                    <section className='Tables__Container Tables--Shadow Tables--Background Instructor__SpaceUp' style={{ maxWidth: '1180px', margin: '0 auto', padding: '24px', borderRadius: '15px' }}>
                        <table className='Tables__Table Instructor__Ztudents__Visible__ContainerTable__Table'>
                            <thead>
                                <tr className='Tables__Table__Row--Header'>
                                    <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__InputColumn'>
                                        <input
                                            type='checkbox'
                                            className='Tables__Table__InputColumn__Input'
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    const newIds = instructorLicenses.map(row => `${row.idCenterUser}-${row.idCourse}`)
                                                    setSelectedLicenseIds(newIds)
                                                    const newEdits = {}
                                                    instructorLicenses.forEach(row => {
                                                        newEdits[`${row.idCenterUser}-${row.idCourse}`] = String(row.licenses)
                                                    })
                                                    setEditedLicenses(newEdits)
                                                } else {
                                                    setSelectedLicenseIds([])
                                                    setEditedLicenses({})
                                                }
                                            }}
                                            checked={instructorLicenses.length > 0 && instructorLicenses.every(row => selectedLicenseIds.includes(`${row.idCenterUser}-${row.idCourse}`))}
                                        />
                                    </td>
                                    <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__Header--Sortable Tables__Table__TextColumn Instructor__Ztudents__Visible__ContainerTable__Table__TextColumn' style={{ width: '280px', whiteSpace: 'nowrap' }} onClick={() => HandleSort('instructorName')}>Nombre del Instructor <SortIndicator sortConfig={sortConfig} columnKey='instructorName' /></td>
                                    <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__Header--Sortable Tables__Table__TextColumn Instructor__Ztudents__Visible__ContainerTable__Table__TextColumn' style={{ width: '150px' }} onClick={() => HandleSort('user')}>Usuario <SortIndicator sortConfig={sortConfig} columnKey='user' /></td>
                                    <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__Header--Sortable Tables__Table__TextColumn Instructor__Ztudents__Visible__ContainerTable__Table__TextColumn' style={{ width: '350px', whiteSpace: 'nowrap' }} onClick={() => HandleSort('courseName')}>Nombre del curso <SortIndicator sortConfig={sortConfig} columnKey='courseName' /></td>
                                    <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__Header--Sortable Tables__Table__TextColumn text-center' style={{ width: '120px' }} onClick={() => HandleSort('licenses')}>Número de<br />Licencias <SortIndicator sortConfig={sortConfig} columnKey='licenses' /></td>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedInstructorLicenses.map((row) => {
                                    const key = `${row.idCenterUser}-${row.idCourse}`
                                    const isSelected = selectedLicenseIds.includes(key)
                                    return (
                                        <tr key={key} className='Tables__Table__Row'>
                                            <td className='Tables__Table__Row__Cell Tables__Table__InputColumn'>
                                                <input
                                                    type='checkbox'
                                                    className='Tables__Table__InputColumn__Input'
                                                    checked={isSelected}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSelectedLicenseIds(prev => [...prev, key])
                                                            setEditedLicenses(prev => ({
                                                                ...prev,
                                                                [key]: String(row.licenses)
                                                            }))
                                                        } else {
                                                            setSelectedLicenseIds(prev => prev.filter(id => id !== key))
                                                            setEditedLicenses(prev => {
                                                                const copy = { ...prev }
                                                                delete copy[key]
                                                                return copy
                                                            })
                                                        }
                                                    }}
                                                />
                                            </td>
                                            <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Ztudents__Visible__ContainerTable__Table__TextColumn' style={{ width: '280px', whiteSpace: 'nowrap' }}>
                                                {GetInstructorName(row)}
                                            </td>
                                            <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Ztudents__Visible__ContainerTable__Table__TextColumn' style={{ width: '150px' }}>
                                                {GetUserName(row)}
                                            </td>
                                            <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Ztudents__Visible__ContainerTable__Table__TextColumn' style={{ width: '350px', whiteSpace: 'nowrap' }}>
                                                {GetCourseNameWithLevel(row)}
                                            </td>
                                            <td className='Tables__Table__Row__Cell Tables__Table__TextColumn text-center' style={{ width: '120px' }}>
                                                {isSelected ? (
                                                    <input
                                                        type='text'
                                                        className='Form__Input text-center'
                                                        style={{
                                                            width: '80px',
                                                            padding: '4px 8px',
                                                            borderRadius: '6px',
                                                            border: '1px solid #e61b72',
                                                            display: 'inline-block',
                                                            margin: 0
                                                        }}
                                                        value={editedLicenses[key] ?? String(row.licenses)}
                                                        onChange={(e) => {
                                                            const val = e.target.value
                                                            if (/^\d*$/.test(val)) {
                                                                setEditedLicenses(prev => ({
                                                                    ...prev,
                                                                    [key]: val
                                                                }))
                                                            }
                                                        }}
                                                    />
                                                ) : (
                                                    <span className='fw-bold'>{row.licenses}</span>
                                                )}
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </section>
                    </>
                )}
            </section>

            {showConfirmationModal && (
                <Confirm
                    message={confirmModalMessage}
                    actionToYesButton={ExecuteUpdateLicenses}
                    textYesButton='Sí, actualizar licencias'
                    textNoButton='No'
                    setShowModal={setShowConfirmationModal}
                    setConfirmData={setLicensesBatchToUpdate}
                />
            )}

            {showUnlinkModal && (
                <Confirm
                    message={unlinkModalMessage}
                    actionToYesButton={ExecuteUnlinkLicenses}
                    textYesButton='Sí, desvincular licencias'
                    textNoButton='No'
                    setShowModal={setShowUnlinkModal}
                    setConfirmData={setLicensesBatchToUnlink}
                />
            )}
        </InstructorBase>
    )
}

export { Licenses };
